/**
 * The facts the Terms and the Privacy Policy both assert, in one place.
 *
 * Both pages used to carry their own copies of the company block and their own
 * prose lists, which is how they drifted: PROF-07's audit found the processor
 * table missing two vendors that user data actually reaches, and an EULA section
 * that named three of App Store Guideline 1.2's four required safeguards.
 *
 * Anything here is a claim we make to users and to App Review, so it is data
 * rather than JSX — `legal.test.ts` checks it against what the app really does
 * (the mobile SDK list, the worker's vendors, the ASC subscription products).
 * Prose that only one page makes stays in that page.
 */

/** Bumped whenever the text below or either legal page's wording changes. */
export const UPDATED = "August 16, 2026";
export const SUPPORT = "support@bibleshield.app";
export const COMPANY = "AKES Solutions Limited";
export const COMPANY_NO = "17091947";
export const COMPANY_ADDRESS =
  "Unit 501 Leroy House, 434-436 Essex Road, London, United Kingdom, N1 3FY";
export const APPLE_EULA =
  "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/";
/** Where Apple sends buyers for refunds — quoted in the billing section. */
export const APPLE_REPORT_PROBLEM = "https://reportaproblem.apple.com";

/**
 * The minimum age both documents state, in one place because they used to state
 * it twice and a translated document would state it eighteen times.
 */
export const MIN_AGE = 13;
/** Intro offer length, as sold. Checked against SUBSCRIPTION_PLANS by the test. */
export const TRIAL_DAYS = 3;

/**
 * How long each thing is kept, as NUMBERS the policy prints rather than prose it
 * repeats.
 *
 * GEN-11: a retention promise is the one part of a privacy policy that is
 * trivially falsifiable — the cron either deletes on that schedule or it does
 * not. The first four values are the worker's own constants
 * (`apps/worker/src/scheduled.ts`), the strike/ban pair is the moderation
 * ladder's (`packages/shared/src/moderation.ts`), and `dataMap.test.ts` reads
 * both files and fails if any of them drifts from what is written here.
 *
 * The two vendor-side windows have no constant to check against: nothing in this
 * repo enforces them, they are project settings in PostHog and Sentry. They are
 * stated as ceilings for that reason.
 */
export const RETENTION = {
  /** Prayer history kept on the server for restore. */
  prayerDays: 180,
  /** Chat history behind the live room. */
  chatDays: 90,
  /** Rows in the in-app notification centre. */
  notificationDays: 90,
  /** Devices that opened the app once and never finished onboarding. */
  ghostDays: 7,
  /** Window a removal counts as a strike in. */
  strikeWindowDays: 90,
  /** The first restriction the ladder hands down (strike 3). */
  firstBanDays: 7,
  /** The longest restriction the ladder can hand down — nothing is permanent. */
  maxBanDays: 30,
  /** Product analytics, vendor-side setting. */
  analyticsMonths: 12,
  /** Crash + performance diagnostics, vendor-side setting. */
  diagnosticsMonths: 12,
} as const;

/**
 * A third-party processor row: name + purpose + link to its privacy policy.
 *
 * `sdk` names the package or server-side integration that makes the vendor real,
 * so the test can start from what the app ships rather than from this list —
 * a processor nobody can point at is either stale or was never true.
 */
export interface Processor {
  name: string;
  purpose: string;
  href: string;
  /** Mobile package names / server integrations that put data in their hands. */
  sdk: string[];
}

export const PROCESSORS: Processor[] = [
  {
    name: "Cloudflare",
    purpose: "Hosting, database, file storage, and content delivery",
    href: "https://www.cloudflare.com/privacypolicy/",
    sdk: ["worker:d1", "worker:kv", "worker:r2"],
  },
  {
    name: "Apple",
    purpose: "App distribution, push delivery, and subscription billing",
    href: "https://www.apple.com/legal/privacy/",
    sdk: ["store:apple"],
  },
  {
    name: "Google",
    purpose:
      "Play distribution and billing, Firebase App Check, and AI prayer generation and text moderation (Gemini)",
    href: "https://policies.google.com/privacy",
    sdk: [
      "@react-native-firebase/app",
      "@react-native-firebase/app-check",
      "worker:gemini",
    ],
  },
  {
    name: "RevenueCat",
    purpose: "Subscription management and receipt validation",
    href: "https://www.revenuecat.com/privacy/",
    sdk: ["react-native-purchases"],
  },
  {
    name: "Expo",
    purpose: "Delivering push notifications",
    href: "https://expo.dev/privacy",
    sdk: ["expo-notifications"],
  },
  {
    name: "PostHog",
    purpose:
      "Product analytics (aggregated usage events), in the app and on bibleshield.app",
    href: "https://posthog.com/privacy",
    // `posthog-js` is the WEBSITE half. It was added when the shared-link pages
    // became the top of the install funnel and needed measuring; this row has to
    // name it, because the policy is what tells a visitor to this page that the
    // page itself is instrumented.
    sdk: ["posthog-react-native", "posthog-js", "@posthog/react"],
  },
  {
    name: "Sentry",
    purpose: "Crash and performance diagnostics",
    href: "https://sentry.io/privacy/",
    sdk: ["@sentry/react-native"],
  },
  {
    // Undisclosed until PROF-07. The SDK ships in production builds, requests the
    // IDFA behind the ATT prompt, and its install id is handed to RevenueCat.
    name: "AppsFlyer",
    purpose:
      "Install attribution and campaign measurement (only after you allow tracking)",
    href: "https://www.appsflyer.com/legal/services-privacy-policy/",
    sdk: ["react-native-appsflyer", "expo-tracking-transparency"],
  },
  {
    // Undisclosed until PROF-07, and the one that mattered most: this vendor
    // receives the actual photo a user uploads as their avatar.
    name: "fal.ai",
    purpose: "Automated screening of uploaded profile photos for adult content",
    href: "https://fal.ai/privacy",
    sdk: ["worker:fal"],
  },
  {
    name: "HelloAO / Free Use Bible API",
    purpose:
      "Public Scripture text, fetched by our servers — it receives no personal information",
    href: "https://bible.helloao.org/",
    sdk: ["worker:helloao"],
  },
];

/**
 * The four things App Store Guideline 1.2 requires an app with user-generated
 * content to do, each paired with how Bible Shield actually does it. Rendered as
 * Terms §5; App Review reads that section looking for exactly this list.
 */
export const UGC_SAFEGUARDS: { label: string; detail: string }[] = [
  {
    label: "Filtering",
    detail:
      "posts, prayer requests, notes, and chat messages are screened automatically before they appear — a word filter on every surface, an AI content classifier on longer writing, and an adult-content check on every uploaded profile photo",
  },
  {
    label: "Reporting",
    detail:
      "every post, comment, message, and user can be reported from inside the app, in two taps",
  },
  {
    label: "Blocking",
    detail:
      "you can block any user, which immediately hides their posts, comments, and messages from you and yours from them",
  },
  {
    label: "Acting within 24 hours",
    detail:
      "we review reports and remove objectionable content and the users who post it, typically within 24 hours",
  },
];

/**
 * The subscription products as they exist in App Store Connect (group "Bible
 * Shield Pro", 175 territories). Deliberately NO price: StoreKit charges in the
 * buyer's currency across 175 storefronts, so any figure written here is a false
 * statement in 174 of them — the app shows the real one on the paywall.
 */
export const SUBSCRIPTION_PLANS: {
  name: string;
  duration: string;
  trial: string | null;
}[] = [
  {
    name: "Bible Shield Pro Weekly",
    duration: "renews every week",
    trial: "3-day free trial",
  },
  {
    name: "Bible Shield Pro Yearly",
    duration: "renews every year",
    trial: "3-day free trial",
  },
];
